import { Directive, Input, OnInit } from '@angular/core';
import { CheckpointModel } from '@fi-app/checklist/checkpoint.model';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { removeItem } from '@fi-shared/utils/remove-item';
import { getLastItem } from '@fi-shared/utils/get-last-Item';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface IChecklistSection {
    tag: {
        Id: number;
        Name: string;
    };
    CheckPoints: CheckpointModel[];
}

@Directive({
    selector: '[fi-checklist-sections]',
    exportAs: 'fi-checklist-sections',
    standalone: false,
})
export class FiChecklistSectionsDirective implements OnInit {
    @Input('fi-checklist-sections') checklist: ChecklistModel;
    @Input() sortNotApplicable: boolean = false;

    fiChecklistSections: IChecklistSection[] = [];

    ngOnInit() {
        this.rebuild();
    }

    rebuild() {
        this.buildSections(this.checklist, this.sortNotApplicable);
        this.updateUsedTagIds();
        this.updateCheckpointsFromSections();
    }

    isSetNotApplicationParser(checkpoint: CheckpointModel) {
        return checkpoint.CheckPointSetting && checkpoint.CheckPointSetting.IsSetNotApplicable;
    }

    _buildSectionsGetWithTagId(tag) {
        const existing = this.fiChecklistSections.find((section) => section.tag.Id == tag.Id);
        if (!existing) {
            const newTag = {
                tag: tag,
                CheckPoints: [],
            };
            this.fiChecklistSections.push(newTag);
            return newTag;
        }
        return existing;
    }

    buildSections(checklist: ChecklistModel, sortNotApplicable) {
        this.fiChecklistSections = [this.createEmptySection()];
        checklist.CheckPoints.forEach((value) => {
            const section = this._buildSectionsGetWithTagId({
                Id: value.TagId,
                Name: value.Tag ? value.Tag.Name : null,
            });

            section.CheckPoints.push(value);
        });

        if (sortNotApplicable) {
            // https://chainformation.atlassian.net/browse/FI-1684
            // not applicable checkpoint should be at the bottom
            this.fiChecklistSections.forEach((section: IChecklistSection) => {
                section.CheckPoints = section.CheckPoints.sort((a, b) => {
                    const aIsSetNotApplicable = this.isSetNotApplicationParser(a);
                    const bIsSetNotApplicable = this.isSetNotApplicationParser(b);

                    if (aIsSetNotApplicable && !bIsSetNotApplicable) return 1;

                    if (!aIsSetNotApplicable && bIsSetNotApplicable) return -1;

                    const aIdx = checklist.CheckPoints.indexOf(a);
                    const bIdx = checklist.CheckPoints.indexOf(b);
                    return aIdx - bIdx;
                });
            });
        }
    }

    onSectionAdd($event) {
        const existingSection = this.fiChecklistSections.find((section) => section.tag.Id === $event.Id);
        if (existingSection) {
            this.sectionRemoveClick(existingSection);
            return;
        }

        if (this.fiChecklistSections.length === 1 && this.fiChecklistSections[0].tag.Id === null) {
            // empty section should stay at the top with "Add checkpoint" button after adding the fist section
            this.fiChecklistSections[0].CheckPoints = [];

            this.fiChecklistSections.push({
                tag: $event,
                CheckPoints: this.checklist.CheckPoints.slice(),
            });

            this.checklist.CheckPoints.forEach(function (checkpoint) {
                checkpoint.TagId = $event.Id;
                checkpoint.Tag = $event;
            });
        } else {
            this.fiChecklistSections.push({
                tag: $event,
                CheckPoints: [],
            });
        }

        this.updateUsedTagIds();
    }

    usedTagIds = [];

    updateUsedTagIds() {
        this.usedTagIds = this.fiChecklistSections.reduce(function (prev, current) {
            if (current.tag.Id) {
                return prev.concat([current.tag.Id]);
            }
            return prev;
        }, []);
    }

    onSectionDrop($event: CdkDragDrop<IChecklistSection, any>) {
        moveItemInArray(this.fiChecklistSections, $event.previousIndex, $event.currentIndex);
        this.updateCheckpointsFromSections();
    }

    sectionRemoveClick(section: IChecklistSection) {
        removeItem(this.fiChecklistSections, section);
        // move all checkpoints from removed section to the top of checklist into section without name
        if (this.fiChecklistSections.length === 0 || this.fiChecklistSections[0].tag.Id !== null) {
            this.fiChecklistSections.unshift(this.createEmptySection());
        }

        const sectionToMoveCheckPoints = this.fiChecklistSections[0];
        sectionToMoveCheckPoints.CheckPoints = sectionToMoveCheckPoints.CheckPoints.concat(section.CheckPoints);
        section.CheckPoints.forEach(function (checkPoint) {
            checkPoint.TagId = null;
            checkPoint.Tag = null;
        });
        this.updateUsedTagIds();
        this.updateCheckpointsFromSections();
    }

    updateCheckpointsFromSections() {
        let i = 0;
        this.checklist.CheckPoints.length = 0;
        this.fiChecklistSections.forEach((section) => {
            section.CheckPoints.forEach((checkpoint) => {
                checkpoint.SortIndex = i++;
                checkpoint.TagId = section.tag.Id;
                checkpoint.Tag = section.tag.Id ? section.tag : null;
                this.checklist.CheckPoints.push(checkpoint);
            });
        });
    }

    checkpointMoveUp(section: IChecklistSection, $index) {
        const checkpointToMove = section.CheckPoints.splice($index, 1)[0];
        if ($index === 0) {
            const sectionToAdd = this.fiChecklistSections[this.fiChecklistSections.indexOf(section) - 1];
            sectionToAdd.CheckPoints.push(checkpointToMove);
        } else {
            section.CheckPoints.splice($index - 1, 0, checkpointToMove);
        }
        this.updateCheckpointsFromSections();
    }

    checkpointMoveDown(section: IChecklistSection, $index) {
        const maxIndex = section.CheckPoints.length - 1,
            checkpointToMove = section.CheckPoints.splice($index, 1)[0];
        if ($index === maxIndex) {
            const sectionToAdd = this.fiChecklistSections[this.fiChecklistSections.indexOf(section) + 1];
            sectionToAdd.CheckPoints.unshift(checkpointToMove);
        } else {
            section.CheckPoints.splice($index + 1, 0, checkpointToMove);
        }
        this.updateCheckpointsFromSections();
    }

    onCheckpointDrop($event: CdkDragDrop<CheckpointModel, any>, section: IChecklistSection) {
        moveItemInArray(section.CheckPoints, $event.previousIndex, $event.currentIndex);
    }

    sectionMoveUp(section: IChecklistSection) {
        const $index = this.fiChecklistSections.indexOf(section);

        if ($index === 1) {
            // https://chainformation.atlassian.net/browse/FI-3463
            // we should move all checkpoints from section with empty tag (checkpoints without section) to current section
            const currentSection = this.fiChecklistSections[$index];
            const emptyNameSection = this.fiChecklistSections[$index - 1];
            emptyNameSection.CheckPoints.splice(0, emptyNameSection.CheckPoints.length).forEach((checkPointToMove) => {
                currentSection.CheckPoints.push(checkPointToMove);
            });
        } else {
            this.fiChecklistSections.splice($index - 1, 0, this.fiChecklistSections.splice($index, 1)[0]);
        }
        this.updateCheckpointsFromSections();
    }

    sectionMoveDown(section: IChecklistSection) {
        const $index = this.fiChecklistSections.indexOf(section);
        this.fiChecklistSections.splice($index + 1, 0, this.fiChecklistSections.splice($index, 1)[0]);
        this.updateCheckpointsFromSections();
    }

    removeCheckpoint(section: IChecklistSection, checkpoint: CheckpointModel) {
        removeItem(section.CheckPoints, checkpoint);
        this.updateCheckpointsFromSections();
    }

    getCheckpointMinPosition() {
        // add additional slot at the top if the first section is empty
        return this.fiChecklistSections[0].CheckPoints.length === 0 ? -1 : 0;
    }

    getCheckpointMaxPosition() {
        const baseMax = this.checklist.CheckPoints.length - 1;
        // add additional slot if the latest section is empty
        return getLastItem(this.fiChecklistSections).CheckPoints.length === 0 ? baseMax + 1 : baseMax;
    }

    createEmptySection() {
        return {
            tag: {
                Id: null,
                Name: null,
            },
            CheckPoints: [],
        };
    }

    getChildPayload(section) {
        return (index) => {
            return section.CheckPoints[index];
        };
    }
}
